import type { V2AiJob } from '../domain/aiJob';
import type { V2AiObservation, V2AiRun } from '../domain/aiObservation';
import type { V2Evidence } from '../domain/evidence';
import type { V2Finding } from '../domain/finding';
import { coverMediaKey, type V2Area, type V2ChecklistItem, type V2Inspection } from '../domain/inspection';
import { agreementPdfMediaKey, reportMediaKey, type V2ReportRecord } from '../domain/report';
import { accountDatabaseName, activeDatabaseName } from './accountScope';
import { assertV2Namespace, V2_DATABASE_NAME, V2_INSPECTION_DATA_RESET_KEY } from './persistenceContract';
import type { RawSnapshot, V2StoreName } from './schema';

// IndexedDB wrapper for the database of the ACTIVE ACCOUNT (src/persistence/accountScope.ts — one
// database per account since 2026-09-21; every function below opens activeDatabaseName()).
// Connections are opened per operation
// and closed in finally — no long-lived handle, no versionchange deadlocks, no stale
// connections after tab restore. Every mutating action of the app is ONE transaction
// (putRecords / deleteRecords / deleteInspectionCascade), so a reload never sees half a save.
//
// DB version 2 (Phase 1): + evidence, findings, media (photo bytes keyed by evidence id).
// DB version 3 (Mobile Owner Test build): + reports (final PDF records; bytes in media under
// `report:<id>`). Record schema versions are a separate concept — see src/persistence/schema.ts.
// DB version 4 (Owner Test Round 2): + aiRuns, aiSuggestions (the AI analysis trail).
// DB version 5 (Advanced AI background jobs, 2026-09-13): + aiJobs.
// DB version 6 (Final Owner Decision 2026-09-14, ratification CA-17 / D-CA-8): the OLD INSPECTION
// DATA RESET. Every object store of the previous versions holds inspection-domain business data —
// development / test inspections the Owner ruled worthless — and the old suggestion / finding /
// job contracts they were written under are gone. The upgrade counts what each old store held,
// deletes every store, and recreates the stores of the new contract (`aiObservations` replaces
// `aiSuggestions`). Nothing outside this database is touched: the auth session, the App Language and
// the inspector profile live in their own localStorage keys and stay.
// DB version 7 (account isolation, Owner device test 2026-09-21): + meta — the one key 'owner' names
// the account a database belongs to. Additive: no existing store is touched by this upgrade.
export const DB_VERSION = 7;

export type { V2StoreName } from './schema';
export type V2Record = V2Inspection | V2Area | V2ChecklistItem | V2Evidence | V2Finding | V2ReportRecord | V2AiRun | V2AiObservation | V2AiJob;

export interface RecordWrite {
  store: V2StoreName;
  value: V2Record;
}
export interface RecordDelete {
  store: V2StoreName;
  key: string;
}

const RECORD_STORES: readonly V2StoreName[] = ['inspections', 'areas', 'items', 'evidence', 'findings', 'reports', 'aiRuns', 'aiObservations', 'aiJobs'];
const MEDIA_STORE = 'media';
const META_STORE = 'meta';
const OWNER_KEY = 'owner';
const THUMB_PREFIX = 'thumb:';

// The reset ledger (D-CA-8): what the version-6 upgrade deleted, per old store, kept on the device so
// the reset is auditable there (Settings → Sync & Storage) and in the closure report.
export interface InspectionDataResetLedger {
  resetAt: number;
  fromVersion: number;
  toVersion: number;
  counts: Record<string, number>;
}

function writeResetLedger(ledger: InspectionDataResetLedger): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(V2_INSPECTION_DATA_RESET_KEY, JSON.stringify(ledger));
  } catch {
    // the ledger is an audit convenience; a storage refusal must not fail the upgrade
  }
}

export function readInspectionDataResetLedger(): InspectionDataResetLedger | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(V2_INSPECTION_DATA_RESET_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const rec = parsed as Record<string, unknown>;
    const counts = typeof rec.counts === 'object' && rec.counts !== null ? (rec.counts as Record<string, unknown>) : {};
    const cleaned: Record<string, number> = {};
    for (const [k, v] of Object.entries(counts)) if (typeof v === 'number') cleaned[k] = v;
    return { resetAt: typeof rec.resetAt === 'number' ? rec.resetAt : 0, fromVersion: typeof rec.fromVersion === 'number' ? rec.fromVersion : 0, toVersion: typeof rec.toVersion === 'number' ? rec.toVersion : DB_VERSION, counts: cleaned };
  } catch {
    return null;
  }
}

// The version-6 upgrade (CA-17): count every old store, then — once the last count is in and still
// inside the versionchange transaction — delete every old store and create the stores of the new
// contract. The ledger is written with the counts.
function createCurrentStores(db: IDBDatabase): void {
  for (const store of RECORD_STORES) {
    if (!db.objectStoreNames.contains(store)) db.createObjectStore(store, { keyPath: 'id' });
  }
  if (!db.objectStoreNames.contains(MEDIA_STORE)) db.createObjectStore(MEDIA_STORE);
  if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
}

function resetInspectionDomain(db: IDBDatabase, tx: IDBTransaction, oldVersion: number): void {
  const existing = Array.from(db.objectStoreNames);
  const counts: Record<string, number> = {};
  let pending = existing.length;
  const finish = () => {
    for (const name of Array.from(db.objectStoreNames)) db.deleteObjectStore(name);
    createCurrentStores(db);
    writeResetLedger({ resetAt: Date.now(), fromVersion: oldVersion, toVersion: DB_VERSION, counts });
  };
  if (pending === 0) {
    finish();
    return;
  }
  for (const name of existing) {
    const request = tx.objectStore(name).count();
    request.onsuccess = () => {
      counts[name] = request.result;
      pending -= 1;
      if (pending === 0) finish();
    };
    request.onerror = () => {
      counts[name] = -1;
      pending -= 1;
      if (pending === 0) finish();
    };
  }
}

function openDb(name: string): Promise<IDBDatabase> {
  assertV2Namespace(name);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      const tx = request.transaction;
      if (event.oldVersion > 0 && event.oldVersion < 6 && tx) resetInspectionDomain(db, tx, event.oldVersion);
      else createCurrentStores(db);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error(`open ${name} failed`));
    request.onblocked = () => reject(new Error(`open ${name} blocked`));
  });
}

// Every record / media function works on the ACTIVE ACCOUNT's database. The name is read when the
// operation starts: with no account in strict mode it throws before anything is opened.
async function withDb<T>(work: (db: IDBDatabase) => Promise<T>): Promise<T> {
  return withNamedDb(activeDatabaseName(), work);
}

async function withNamedDb<T>(name: string, work: (db: IDBDatabase) => Promise<T>): Promise<T> {
  const db = await openDb(name);
  try {
    return await work(db);
  } finally {
    db.close();
  }
}

function requestDone<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  });
}

// ---------- records ----------

export function putRecords(writes: readonly RecordWrite[], deletes: readonly RecordDelete[] = []): Promise<void> {
  if (writes.length === 0 && deletes.length === 0) return Promise.resolve();
  return withDb(async (db) => {
    const stores = [...new Set([...writes.map((w) => w.store), ...deletes.map((d) => d.store)])];
    const tx = db.transaction(stores, 'readwrite');
    const done = txDone(tx);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    for (const d of deletes) tx.objectStore(d.store).delete(d.key);
    await done;
  });
}

export function putRecord(store: V2StoreName, value: V2Record): Promise<void> {
  return putRecords([{ store, value }]);
}

export function loadRawSnapshot(): Promise<RawSnapshot> {
  return withDb(async (db) => {
    const tx = db.transaction([...RECORD_STORES], 'readonly');
    const [inspections, areas, items, evidence, findings, reports, aiRuns, aiObservations, aiJobs] = await Promise.all(
      RECORD_STORES.map((store) => requestDone(tx.objectStore(store).getAll() as IDBRequest<unknown[]>))
    );
    return { inspections, areas, items, evidence, findings, reports, aiRuns, aiObservations, aiJobs };
  });
}

// Row counts of every store (the reset evidence and the fresh-start proof).
export function countRecords(): Promise<Record<string, number>> {
  return withDb(async (db) => {
    const names = [...RECORD_STORES, MEDIA_STORE];
    const tx = db.transaction(names, 'readonly');
    const counts = await Promise.all(names.map((store) => requestDone(tx.objectStore(store).count())));
    return Object.fromEntries(names.map((name, index) => [name, counts[index]]));
  });
}

// ---------- media (photo bytes) ----------

// Bytes are stored as ArrayBuffer + mime type (not Blob): structured-cloneable everywhere,
// including WebKit's historically fragile Blob-in-IndexedDB path and the in-memory test DB.
export interface MediaBytes {
  type: string;
  bytes: ArrayBuffer;
}

export interface MediaWrite {
  evidenceId: string;
  full: MediaBytes;
  thumbnail: MediaBytes | null;
}

// Brand check instead of instanceof: a structured clone can hand back an ArrayBuffer from a
// different realm (observed with the in-memory test database), and instanceof would miss it.
function isMediaBytes(value: unknown): value is MediaBytes {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.toString.call((value as MediaBytes).bytes) === '[object ArrayBuffer]' &&
    typeof (value as MediaBytes).type === 'string'
  );
}

// Evidence record + bytes land in ONE transaction: a photo can never exist in the record store
// without its bytes, or the other way round.
export function putEvidenceWithMedia(media: MediaWrite, writes: readonly RecordWrite[]): Promise<void> {
  return withDb(async (db) => {
    const stores = [...new Set([MEDIA_STORE, ...writes.map((w) => w.store)])];
    const tx = db.transaction(stores, 'readwrite');
    const done = txDone(tx);
    const mediaStore = tx.objectStore(MEDIA_STORE);
    mediaStore.put(media.full, media.evidenceId);
    if (media.thumbnail) mediaStore.put(media.thumbnail, `${THUMB_PREFIX}${media.evidenceId}`);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    await done;
  });
}

export function getMedia(evidenceId: string, variant: 'full' | 'thumbnail' = 'full'): Promise<Blob | null> {
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readonly');
    const key = variant === 'full' ? evidenceId : `${THUMB_PREFIX}${evidenceId}`;
    const value = await requestDone(tx.objectStore(MEDIA_STORE).get(key) as IDBRequest<unknown>);
    return isMediaBytes(value) ? new Blob([new Uint8Array(value.bytes)], { type: value.type }) : null;
  });
}

// ---------- moving one inspection to another device (Owner 2026-10-20) ----------

// The stored bytes of one media key exactly as kept (src/state/inspectionTransfer.ts writes them into the file).
export function getMediaEntry(key: string): Promise<MediaBytes | null> {
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readonly');
    const value = await requestDone(tx.objectStore(MEDIA_STORE).get(key) as IDBRequest<unknown>);
    return isMediaBytes(value) ? { type: value.type, bytes: value.bytes } : null;
  });
}

// Bytes brought in by a file, a batch at a time. The records that point at them are written LAST, in one
// transaction (putRecords), so a reload never sees an imported inspection whose photos are missing.
export function putMediaEntries(entries: readonly { key: string; value: MediaBytes }[]): Promise<void> {
  if (entries.length === 0) return Promise.resolve();
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readwrite');
    const done = txDone(tx);
    const store = tx.objectStore(MEDIA_STORE);
    for (const entry of entries) store.put(entry.value, entry.key);
    await done;
  });
}

// Undoes putMediaEntries() of an import that could not finish.
export function deleteMediaEntries(keys: readonly string[]): Promise<void> {
  if (keys.length === 0) return Promise.resolve();
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readwrite');
    const done = txDone(tx);
    const store = tx.objectStore(MEDIA_STORE);
    for (const key of keys) store.delete(key);
    await done;
  });
}

export function thumbnailKeyOf(key: string): string {
  return `${THUMB_PREFIX}${key}`;
}

// ---------- standalone media (report cover photo · inspector avatar) ----------

// Bytes that belong to something other than an evidence row: the report cover photo of an inspection
// (key coverMediaKey(id), record update on the inspection) and the device inspector's own photo
// (src/state/inspectorPhoto.ts, referenced by the localStorage profile). Bytes and record updates land
// in ONE transaction; reads go through getMedia() like every other media key.
export function putMediaWithRecords(key: string, full: MediaBytes, thumbnail: MediaBytes | null, writes: readonly RecordWrite[] = []): Promise<void> {
  return withDb(async (db) => {
    const stores = [...new Set([MEDIA_STORE, ...writes.map((w) => w.store)])];
    const tx = db.transaction(stores, 'readwrite');
    const done = txDone(tx);
    const mediaStore = tx.objectStore(MEDIA_STORE);
    mediaStore.put(full, key);
    if (thumbnail) mediaStore.put(thumbnail, `${THUMB_PREFIX}${key}`);
    else mediaStore.delete(`${THUMB_PREFIX}${key}`);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    await done;
  });
}

// Removes the app's own bytes (+ the record update that drops the reference). The system album is
// never involved (OWNER-CONTRACT §6.1.2).
export function deleteMediaWithRecords(key: string, writes: readonly RecordWrite[] = []): Promise<void> {
  return withDb(async (db) => {
    const stores = [...new Set([MEDIA_STORE, ...writes.map((w) => w.store)])];
    const tx = db.transaction(stores, 'readwrite');
    const done = txDone(tx);
    const mediaStore = tx.objectStore(MEDIA_STORE);
    mediaStore.delete(key);
    mediaStore.delete(`${THUMB_PREFIX}${key}`);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    await done;
  });
}

// Removes the app's own record + bytes (+ any record updates that unlink it) in one transaction.
// This is the ONLY delete path for photos and it touches nothing outside this database — the
// system album is never involved (OWNER-CONTRACT §6.1.2).
export function deleteEvidenceWithMedia(evidenceId: string, writes: readonly RecordWrite[]): Promise<void> {
  return withDb(async (db) => {
    const stores = [...new Set([MEDIA_STORE, 'evidence' as V2StoreName, ...writes.map((w) => w.store)])];
    const tx = db.transaction(stores, 'readwrite');
    const done = txDone(tx);
    const mediaStore = tx.objectStore(MEDIA_STORE);
    mediaStore.delete(evidenceId);
    mediaStore.delete(`${THUMB_PREFIX}${evidenceId}`);
    tx.objectStore('evidence').delete(evidenceId);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    await done;
  });
}

// ---------- final report PDFs ----------

// Report record + PDF bytes (+ the inspection lock write) land in ONE transaction: a report record
// never exists without its file, and the inspection is never locked without its report.
export function putReportWithMedia(record: V2ReportRecord, pdf: MediaBytes, writes: readonly RecordWrite[] = []): Promise<void> {
  return withDb(async (db) => {
    const stores = [...new Set([MEDIA_STORE, 'reports' as V2StoreName, ...writes.map((w) => w.store)])];
    const tx = db.transaction(stores, 'readwrite');
    const done = txDone(tx);
    tx.objectStore(MEDIA_STORE).put(pdf, reportMediaKey(record.id));
    tx.objectStore('reports').put(record);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    await done;
  });
}

// NY F1 Round 5 (Owner ruling OD-12): the LAST PDF of a generation run, its bytes, the finalized and
// purged inspection record and the deletion of every working record of that inspection land in ONE
// transaction. "Report generated", "locked" and "working data deleted" are therefore never observed
// apart, and a crash in the middle leaves the inspection exactly as it was.
// The media deleted here is the app's own copy of the photos; the device photo album is not involved
// (there is no code path in this app that can delete from the album).
export interface WorkingDataKeys {
  areaIds: readonly string[];
  itemIds: readonly string[];
  evidenceIds: readonly string[];
  findingIds: readonly string[];
  aiRunIds: readonly string[];
  aiObservationIds: readonly string[];
  aiJobIds: readonly string[];
}

export function putFinalReportAndPurge(record: V2ReportRecord, pdf: MediaBytes, writes: readonly RecordWrite[], keys: WorkingDataKeys): Promise<void> {
  return withDb(async (db) => {
    const tx = db.transaction([...RECORD_STORES, MEDIA_STORE], 'readwrite');
    const done = txDone(tx);
    const mediaStore = tx.objectStore(MEDIA_STORE);
    mediaStore.put(pdf, reportMediaKey(record.id));
    // the agreement PDF made at signing is now one of the report records (or was just used for it)
    mediaStore.delete(agreementPdfMediaKey(record.inspectionId));
    tx.objectStore('reports').put(record);
    for (const w of writes) tx.objectStore(w.store).put(w.value);
    for (const id of keys.itemIds) tx.objectStore('items').delete(id);
    for (const id of keys.findingIds) tx.objectStore('findings').delete(id);
    for (const id of keys.areaIds) tx.objectStore('areas').delete(id);
    for (const id of keys.aiRunIds) tx.objectStore('aiRuns').delete(id);
    for (const id of keys.aiObservationIds) tx.objectStore('aiObservations').delete(id);
    for (const id of keys.aiJobIds) tx.objectStore('aiJobs').delete(id);
    for (const id of keys.evidenceIds) {
      tx.objectStore('evidence').delete(id);
      mediaStore.delete(id);
      mediaStore.delete(`${THUMB_PREFIX}${id}`);
    }
    await done;
  });
}

// Owner 2026-10-21: the signed agreement's PDF, rendered once right after signing and kept until the
// final report takes it over. `fingerprint` says which agreement text it was rendered from.
export interface StoredAgreementPdf extends MediaBytes {
  fingerprint: string;
  pageCount: number;
  generatedAt: number;
}

export function putAgreementPdf(inspectionId: string, pdf: StoredAgreementPdf): Promise<void> {
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readwrite');
    const done = txDone(tx);
    tx.objectStore(MEDIA_STORE).put(pdf, agreementPdfMediaKey(inspectionId));
    await done;
  });
}

export function getAgreementPdf(inspectionId: string): Promise<StoredAgreementPdf | null> {
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readonly');
    const value = await requestDone(tx.objectStore(MEDIA_STORE).get(agreementPdfMediaKey(inspectionId)) as IDBRequest<unknown>);
    if (!isMediaBytes(value)) return null;
    const rec = value as Partial<StoredAgreementPdf>;
    if (typeof rec.fingerprint !== 'string' || typeof rec.pageCount !== 'number' || typeof rec.generatedAt !== 'number') return null;
    return { type: value.type, bytes: value.bytes, fingerprint: rec.fingerprint, pageCount: rec.pageCount, generatedAt: rec.generatedAt };
  });
}

export function getReportBytes(reportId: string): Promise<Blob | null> {
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readonly');
    const value = await requestDone(tx.objectStore(MEDIA_STORE).get(reportMediaKey(reportId)) as IDBRequest<unknown>);
    return isMediaBytes(value) ? new Blob([new Uint8Array(value.bytes)], { type: value.type }) : null;
  });
}

// ---------- cascade delete of one inspection ----------

export interface CascadeKeys {
  areaIds: readonly string[];
  itemIds: readonly string[];
  evidenceIds: readonly string[];
  findingIds: readonly string[];
  reportIds?: readonly string[];
  aiRunIds?: readonly string[];
  aiObservationIds?: readonly string[];
  aiJobIds?: readonly string[];
}

export function deleteInspectionCascade(inspectionId: string, keys: CascadeKeys): Promise<void> {
  return withDb(async (db) => {
    const tx = db.transaction([...RECORD_STORES, MEDIA_STORE], 'readwrite');
    const done = txDone(tx);
    tx.objectStore('inspections').delete(inspectionId);
    for (const id of keys.areaIds) tx.objectStore('areas').delete(id);
    for (const id of keys.itemIds) tx.objectStore('items').delete(id);
    for (const id of keys.findingIds) tx.objectStore('findings').delete(id);
    const mediaStore = tx.objectStore(MEDIA_STORE);
    for (const id of keys.evidenceIds) {
      tx.objectStore('evidence').delete(id);
      mediaStore.delete(id);
      mediaStore.delete(`${THUMB_PREFIX}${id}`);
    }
    // Owner 2026-09-14: the report cover photo goes with the inspection (its key is deterministic).
    mediaStore.delete(coverMediaKey(inspectionId));
    mediaStore.delete(`${THUMB_PREFIX}${coverMediaKey(inspectionId)}`);
    mediaStore.delete(agreementPdfMediaKey(inspectionId));
    for (const id of keys.reportIds ?? []) {
      tx.objectStore('reports').delete(id);
      mediaStore.delete(reportMediaKey(id));
    }
    for (const id of keys.aiRunIds ?? []) tx.objectStore('aiRuns').delete(id);
    for (const id of keys.aiObservationIds ?? []) tx.objectStore('aiObservations').delete(id);
    for (const id of keys.aiJobIds ?? []) tx.objectStore('aiJobs').delete(id);
    await done;
  });
}

export function countMediaEntries(): Promise<number> {
  return withDb(async (db) => {
    const tx = db.transaction(MEDIA_STORE, 'readonly');
    return requestDone(tx.objectStore(MEDIA_STORE).count());
  });
}

// ---------- which account a database belongs to (account isolation, 2026-09-21) ----------

// The owner is written INSIDE the database it describes, in the same place as the data, so the claim
// can never be lost apart from the data (a localStorage note could be).
interface DatabaseOwner {
  accountId: string;
  claimedAt: number;
}

function readOwner(db: IDBDatabase): Promise<string | null> {
  const tx = db.transaction(META_STORE, 'readonly');
  return requestDone(tx.objectStore(META_STORE).get(OWNER_KEY) as IDBRequest<unknown>).then((value) => {
    const record = value as Partial<DatabaseOwner> | undefined;
    return record && typeof record.accountId === 'string' && record.accountId.length > 0 ? record.accountId : null;
  });
}

// Writes the owner only while the database has none: a claim is never overwritten. Resolves the owner
// that stands afterwards (the caller's id, or the earlier claimant's).
// Read and write share ONE transaction, and the write is issued from the read's own success callback
// (never after an await), so the transaction is still active on every WebKit this app runs on.
function claimIfUnowned(db: IDBDatabase, accountId: string, now: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readwrite');
    const store = tx.objectStore(META_STORE);
    let owner = accountId;
    const read = store.get(OWNER_KEY) as IDBRequest<unknown>;
    read.onsuccess = () => {
      const record = read.result as Partial<DatabaseOwner> | undefined;
      const standing = record && typeof record.accountId === 'string' && record.accountId.length > 0 ? record.accountId : null;
      if (standing === null) store.put({ accountId, claimedAt: now } satisfies DatabaseOwner, OWNER_KEY);
      else owner = standing;
    };
    tx.oncomplete = () => resolve(owner);
    tx.onerror = () => reject(tx.error ?? new Error('owner claim failed'));
    tx.onabort = () => reject(tx.error ?? new Error('owner claim aborted'));
  });
}

function holdsWork(db: IDBDatabase): Promise<boolean> {
  const names = ['inspections', 'reports', MEDIA_STORE];
  const tx = db.transaction(names, 'readonly');
  return Promise.all(names.map((name) => requestDone(tx.objectStore(name).count()))).then((counts) => counts.some((n) => n > 0));
}

export function databaseOwner(name: string): Promise<string | null> {
  return withNamedDb(name, (db) => readOwner(db));
}

// The database an account works in.
//   1. The database the device had BEFORE accounts were split (V2_DATABASE_NAME) belongs to whoever
//      owns it. An unowned one that holds work is adopted by the account that is signed in now — the
//      first one after the update — so the inspector's existing inspections are not orphaned; nobody
//      else ever opens it again. An unowned EMPTY one is left alone.
//   2. Every other account gets its own database, stamped with its owner the first time it is opened.
// A database whose stamp names somebody else is never returned: the account falls through to its own.
export async function resolveAccountDatabase(accountId: string, now: number = Date.now()): Promise<string> {
  const own = accountDatabaseName(accountId);
  const ownerId = own.slice(V2_DATABASE_NAME.length + 1);
  const legacyOwner = await withNamedDb(V2_DATABASE_NAME, async (db) => {
    const standing = await readOwner(db);
    if (standing !== null) return standing;
    return (await holdsWork(db)) ? claimIfUnowned(db, ownerId, now) : null;
  });
  if (legacyOwner === ownerId) return V2_DATABASE_NAME;
  const standing = await withNamedDb(own, (db) => claimIfUnowned(db, ownerId, now));
  if (standing !== ownerId) throw new Error(`account database ${own} is stamped for another account`);
  return own;
}
